import { useCallback } from 'react';
import { calculateFileProgressStatus } from '../../features/tasks/utils/taskUtils';
import { trackEvent } from '../utils/analytics';

/**
 * Hook for file CRUD and file-related API calls.
 * Requires updateFileOrders and updateLastOpenedFile to be used from within the hook (createFile).
 */
export function useFilesCrud({
  files,
  setFiles,
  currentFileId,
  setCurrentFileId,
  setTasks,
  isLoggedIn,
  tasks,
}) {
  const updateLastOpenedFile = useCallback(
    async (fileId) => {
      if (!isLoggedIn) return;
      try {
        await fetch('/api/files.php', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: fileId }),
        });
      } catch (error) {
        console.error('Error updating last opened file:', error);
      }
    },
    [isLoggedIn]
  );

  const updateFileOrders = useCallback(
    async (fileOrders, optimistic = false) => {
      if (!isLoggedIn) {
        setFiles((prevFiles) => {
          const fileMap = new Map(prevFiles.map((f) => [f.id, f]));
          fileOrders.forEach(({ id, order }) => {
            if (fileMap.has(id)) fileMap.set(id, { ...fileMap.get(id), order });
          });
          return Array.from(fileMap.values());
        });
        return;
      }
      if (optimistic) {
        setFiles((prevFiles) => {
          const fileMap = new Map(prevFiles.map((f) => [f.id, f]));
          fileOrders.forEach(({ id, order }) => {
            if (fileMap.has(id)) fileMap.set(id, { ...fileMap.get(id), order });
          });
          return Array.from(fileMap.values());
        });
      }
      try {
        const response = await fetch('/api/files.php', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batch_orders: fileOrders }),
        });
        if (response.ok) {
          if (!optimistic) {
            setFiles((prevFiles) => {
              const fileMap = new Map(prevFiles.map((f) => [f.id, f]));
              fileOrders.forEach(({ id, order }) => {
                if (fileMap.has(id))
                  fileMap.set(id, { ...fileMap.get(id), order });
              });
              return Array.from(fileMap.values());
            });
          }
          return { success: true };
        }
        const error = await response.json();
        throw new Error(error.error || 'Failed to update file orders');
      } catch (error) {
        console.error('Error updating file orders:', error);
        throw error;
      }
    },
    [isLoggedIn, setFiles]
  );

  const createFile = useCallback(
    async (name, options = {}) => {
      const switchToFile = options.switchToFile !== false;
      let fileName = name.trim();

      if (!isLoggedIn) {
        const tempId = `temp_file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newFile = {
          id: tempId,
          name: fileName,
          last_modified: new Date().toISOString(),
          order: 0,
        };
        setFiles((prevFiles) => {
          const regular = prevFiles.filter((f) => !f.archived);
          const updatedRegular = regular.map((f, i) => ({ ...f, order: i + 1 }));
          const archived = prevFiles.filter((f) => f.archived);
          return [newFile, ...updatedRegular, ...archived];
        });
        if (switchToFile) {
          setCurrentFileId(tempId);
          setTasks([]);
        }
        return newFile;
      }

      try {
        const response = await fetch('/api/files.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: fileName }),
        });
        if (response.ok) {
          const newFile = await response.json();
          const regularFiles = files.filter((f) => !f.archived);
          const fileOrders = [
            { id: newFile.id, order: 0 },
            ...regularFiles.map((f, i) => ({ id: f.id, order: i + 1 })),
          ];
          await updateFileOrders(fileOrders, false);
          setFiles((prev) => [{ ...newFile, order: 0 }, ...prev]);
          if (switchToFile) {
            setCurrentFileId(newFile.id);
            await updateLastOpenedFile(newFile.id);
          }
          trackEvent('file_created');
          if (regularFiles.length === 0) {
            trackEvent('first_list_created');
          }
          return newFile;
        }
        const error = await response.json();
        throw new Error(error.error || 'Failed to create file');
      } catch (error) {
        console.error('Error creating file:', error);
        throw error;
      }
    },
    [
      isLoggedIn,
      files,
      setFiles,
      setCurrentFileId,
      setTasks,
      updateFileOrders,
      updateLastOpenedFile,
    ]
  );

  const updateFileName = useCallback(
    async (fileId, newName) => {
      const fileName = newName.trim();
      if (!isLoggedIn) {
        setFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === fileId ? { ...file, name: fileName } : file
          )
        );
        return { id: fileId, name: fileName };
      }
      try {
        const response = await fetch('/api/files.php', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: fileId, name: fileName }),
        });
        if (response.ok) {
          const updatedFile = await response.json();
          setFiles((prevFiles) =>
            prevFiles.map((file) =>
              file.id === fileId ? { ...file, name: updatedFile.name } : file
            )
          );
          trackEvent('file_renamed');
          return updatedFile;
        }
        const error = await response.json();
        throw new Error(error.error || 'Failed to update file');
      } catch (error) {
        console.error('Error updating file:', error);
        throw error;
      }
    },
    [isLoggedIn, setFiles]
  );

  const toggleArchiveFile = useCallback(
    async (fileId) => {
      if (!isLoggedIn) {
        setFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === fileId ? { ...file, archived: !file.archived } : file
          )
        );
        return;
      }
      const currentFile = files.find((f) => f.id === fileId);
      if (!currentFile) throw new Error('File not found');
      const newArchivedStatus = !currentFile.archived;
      const response = await fetch('/api/files.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: fileId, archived: newArchivedStatus }),
      });
      if (response.ok) {
        const updatedFile = await response.json();
        setFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === fileId
              ? { ...file, archived: updatedFile.archived }
              : file
          )
        );
        trackEvent('file_archived', { archived: newArchivedStatus });
        return updatedFile;
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to update file');
    },
    [isLoggedIn, files, setFiles]
  );

  const updateFileArchiveStatus = useCallback(
    async (fileId, archived, optimistic = false) => {
      if (!isLoggedIn) {
        setFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === fileId ? { ...file, archived } : file
          )
        );
        return;
      }
      if (optimistic) {
        setFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === fileId ? { ...file, archived } : file
          )
        );
      }
      try {
        const response = await fetch('/api/files.php', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: fileId, archived }),
        });
        if (response.ok) {
          const updatedFile = await response.json();
          if (!optimistic) {
            setFiles((prevFiles) =>
              prevFiles.map((file) =>
                file.id === fileId
                  ? { ...file, archived: updatedFile.archived }
                  : file
              )
            );
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((file) =>
                file.id === fileId ? { ...file, ...updatedFile } : file
              )
            );
          }
          return updatedFile;
        }
        const error = await response.json();
        if (optimistic) {
          setFiles((prevFiles) =>
            prevFiles.map((file) =>
              file.id === fileId ? { ...file, archived: !archived } : file
            )
          );
        }
        throw new Error(error.error || 'Failed to update file archive status');
      } catch (error) {
        console.error('Error updating file archive status:', error);
        if (optimistic) {
          setFiles((prevFiles) =>
            prevFiles.map((file) =>
              file.id === fileId ? { ...file, archived: !archived } : file
            )
          );
        }
        throw error;
      }
    },
    [isLoggedIn, setFiles]
  );

  const saveFileProgressStatus = useCallback(
    async (fileId) => {
      if (!isLoggedIn || !fileId || fileId === 'default') return;
      try {
        const progressStatus = calculateFileProgressStatus(tasks, fileId);
        if (progressStatus) {
          const response = await fetch('/api/files.php', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: fileId,
              saved_progress_status: progressStatus,
            }),
          });
          if (response.ok) {
            const updatedFile = await response.json();
            setFiles((prevFiles) =>
              prevFiles.map((file) =>
                file.id === fileId
                  ? { ...file, saved_progress_status: updatedFile.saved_progress_status }
                  : file
              )
            );
          }
        }
      } catch (error) {
        console.error('Error saving file progress status:', error);
      }
    },
    [isLoggedIn, tasks, setFiles]
  );

  return {
    updateLastOpenedFile,
    createFile,
    updateFileName,
    toggleArchiveFile,
    updateFileOrders,
    updateFileArchiveStatus,
    saveFileProgressStatus,
  };
}
